import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	ConfluenceTransactionAccountEndOfTrialQuery$data,
	ConfluenceTransactionAccountEndOfTrialQuery$variables,
} from './__generated__/ConfluenceTransactionAccountEndOfTrialQuery.graphql.ts';
import { ConfluenceTransactionAccountEndOfTrialQuery as TranasctionAccountQuery } from './ConfluenceTransactionAccountEndOfTrialQuery.ts';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

export function useConfluenceTransactionEndOfTrial(
	skip: boolean,
	cloudId: string,
): {
	isEligible: boolean;
	loading: boolean;
	error?: Error;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const canSkipQuery = skip || !cloudId;

	const { data, error, loading } = useQueryInterop<
		ConfluenceTransactionAccountEndOfTrialQuery$data,
		ConfluenceTransactionAccountEndOfTrialQuery$variables
	>(TranasctionAccountQuery, {
		variables: {
			cloudId,
			productKey: 'confluence.ondemand',
		},
		skip: canSkipQuery,
		fetchPolicy: 'cache-first',
		interopApolloAggClient: true,
		onError: (error: Error) => {
			const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
				product: Product.CONFLUENCE,
				team: Team.GROWTH_LUNA,
				experimentMeta: {
					experimentKey: 'twcg-530_confluence_ai_in_editor_eot',
				},
				errorType: ErrorType.GRAPHQL,
				errorMessage: error?.message,
				severity: Severity.MEDIUM,
				source: 'useConfluenceTransactionEndOfTrial',
				additionalAttributes: {
					cloudId,
				},
			});

			createAnalyticsEvent(eventPayload).fire();
		},
	});

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;
	const transactionAccount = entitlement?.transactionAccount;

	const canPay = transactionAccount?.isCurrentUserBillingAdmin === true;
	const hasPaymentDetails = transactionAccount?.isBillToPresent;

	const isEligible = canPay && !hasPaymentDetails;

	return {
		loading,
		error,
		isEligible,
	};
}
